/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { analyticEvents as triggerAnalyticEvent } from "@actions/analyticEvents";
import { PRODUCT_STATE } from "@utils/constants";
import { useRouter } from "next/router";
import { CopilotWithoutTailwind as CopilotWithOutAnimation } from "@acko-swat-sdk/ui";
import { AuthLogin } from "@acko-tech/building-blocks.utils.auth-login-module";
import { authorizeUser } from "@service/authServices";
import { useDispatch, useSelector } from "react-redux";
import { setToastData } from "@store/slices/globalState";

const ALLOWED_PATHS: string[] = [
  "/gi/renew-policy/",
  "/gi/auto-storefront/fresh-car/vehicle-prequote?current_node=enter_mmv_details_node",
  "/gi/auto-storefront/fresh-car/vehicle-prequote?current_node=previous_policy_confirmation_node",
  "/gi/auto-storefront/fresh-car/vehicle-prequote?current_node=previous_claim_confirmation_node",
  "/gi/auto-storefront/fresh-car/vehicle-details?current_node=edit_mmv_details_node",
  "/gi/auto-storefront/fresh-car/vehicle-addons/car?current_node=vehicle_addons_node",
  "/gi/auto-storefront/fresh-car/plans-coverage?current_node=plan_selection_node",
  "/gi/auto-storefront/fresh-car/family-addons/family?current_node=family_addons_node",
  "/gi/auto-storefront/fresh-car?current_node=garage_list_node",
  "/gi/auto-storefront/fresh-car/plans-variants?current_node=plan_variants_node",
  "/gi/auto-storefront/fresh-car/checkout-review?current_node=checkout_review_node",
  "/gi/auto-storefront/fresh-car/vehicle-addons/?current_node=vehicle_addons_node",
  "/gi/auto-storefront/fresh-car/checkout-review?current_node=checkout_edit_and_review__node",
  "/gi/auto-storefront/fresh-car/plans/third-party?current_node=tp_only_plan_node",
  "/gi/auto-storefront/new-car/select-plans?current_node=plan_selection_node",
  "/gi/auto-storefront/new-car/plan_info?current_node=plan_info_node",
  "/gi/auto-storefront/new-car/view-garages?current_node=network_garage_list_node",
  "/gi/auto-storefront/new-car/addons/car?current_node=addons_node",
  "/gi/auto-storefront/new-car/checkout?current_node=checkout_node",
  "/gi/auto-storefront/dashcam/tracking?current_node=dashcam_tracking_node",
  "/gi/auto-storefront/dashcam/dashboard?current_node=installation_tracking_node",
];

const Chatbot = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { asPath, query } = router;

  const { normalisedConfig, normalisedConfigSuccess } = useSelector(({ configuration, globalState }: any) => ({
    normalisedConfig: configuration?.normalisedConfig,
    normalisedConfigSuccess: configuration?.normalisedConfigSuccess,
  }));
  const floatingIconConfig = normalisedConfig?.floating_icon?.props;
  const showCentralChatbotFromConfig = floatingIconConfig?.showCentralChatbot;

  const shouldShowCentralChatbot = useMemo(() => {
    // Hide FAB until config is loaded to prevent showing on page load
    if (!normalisedConfigSuccess) {
      return false;
    }
    
    if (showCentralChatbotFromConfig !== undefined) {
      return showCentralChatbotFromConfig === "control";
    }
    return true;
  }, [showCentralChatbotFromConfig, normalisedConfigSuccess]);
  
  const journey = useMemo(() => {
    if (asPath?.includes("new-car")) {
      return "new_car";
    }
    if (asPath?.includes("dashcam")) {
      return "dashcam";
    }
    if (asPath?.includes("fresh-car")) {
      return "fresh_car";
    }
    return "fresh_car";
  }, [asPath,query]);

  const scope = useMemo(() => {
    if (journey === "new_car") {
      return "auto-newcar";
    }
    return "auto-freshcar";
  }, [journey]);
  const [isCopilotMounted] = useState(true);
  const shouldSendLoginMessageRef = useRef(false);

  // Check on mount if we need to send login message after page refresh (via copilot_open query param)
  useEffect(() => {
    if (query.copilot_open === "true") {
      shouldSendLoginMessageRef.current = true;
      // Remove copilot_open from query params without refresh
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { copilot_open: _, ...restQuery } = query;
      router.replace(
        { pathname: router.pathname, query: restQuery },
        undefined,
        { shallow: true }
      );
    }
  }, [query.copilot_open, router]);

  const redirectToLogin = useCallback(() => {
    // eslint-disable-next-line no-new
    new AuthLogin({
      clientId: "auto-sdui-client",
      onSuccess: async () => {
        try {
          // Call authorize API and wait for it to complete
          await authorizeUser({ proposalEkey: query.proposal_ekey as string, Cookie: document.cookie });
          // Refresh the page with copilot_open=true to open chatbot after reload
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set("copilot_open", "true");
          window.location.href = currentUrl.toString();
        } catch {
          dispatch(setToastData({
            toastMessage: "Login failed. Please try again.",
            toastMessageType: "error",
            timeout: 3000
          }));
        }
      },
      enableScrollOnClose: true
    });
  }, [query.proposal_ekey, dispatch]);

  const copilotEventHandler = useCallback(
    (e: any) => {
      switch (e.data.type) {
        case "copilot-iframe-loaded":
          break;

        case "copilot-agent-callback-init":
          break;

        case "join-conversation":
          if (shouldSendLoginMessageRef.current) {
            shouldSendLoginMessageRef.current = false;
            setTimeout(() => {
              (window as any).copilot.open();
              (window as any).copilot.sendMessage(
                'I have logged in, please continue',
              );
            }, 500);
          }
          break;

      case "copilot-agent-callback-success":
        triggerAnalyticEvent({
          edata: {
            product: "car",
            action_name: "tap_btn_call_scheduled",
            action_type: "tap_btn",
            language: e?.data?.payload?.preferred_language,
            phone: e?.data?.payload?.phoneNumber,
            techinfo: true,
            product_state: PRODUCT_STATE.chatbot_callback
          },
          odata: {
            journey: journey
          },
          event_name: "action"
        });
        break;

      case "copilot-iframe-open":
        triggerAnalyticEvent({
          edata: {
            product: "car",
            view_name: "help_me_popup_loaded",
            view_type: "view_modal",
            product_state: PRODUCT_STATE.chatbot_callback
          },
          odata: {
            journey: journey
          },
          event_name: "view"
        });
        break;

        case "copilot-iframe-close":
          break;

        case "login":
          (window as any).copilot.close();
          redirectToLogin();
          break;

        default:
      }
    },
    [redirectToLogin]
  );
  const currentPath = asPath ? asPath.split("#")[0] : "";

  const isCopilotEnabled = useMemo(() => {
    if (!currentPath) return false;
    return ALLOWED_PATHS.some(
      (path) =>
        currentPath === path ||
        currentPath.startsWith(path + "&") ||
        currentPath.startsWith(path + "?")
    );
  }, [currentPath]);

  useEffect(() => {
    window.addEventListener("message", copilotEventHandler);
    return () => {
      window.removeEventListener("message", copilotEventHandler);
    };
  }, [copilotEventHandler]);

  return (
    isCopilotEnabled && isCopilotMounted && (
      <CopilotWithOutAnimation
        url={`/p/acko-assist-v2/chat?journey=auto-presales&scope=${scope}&call_button=true`}
        fabHide={journey === "dashcam" || !shouldShowCentralChatbot} 
        onEvent={() => {}}
        zIndex={500}
      />
    )
  );
};

export default Chatbot;
